<script setup>
import { computed, ref, watch } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import BnLoading from '@/components/BnLoading.vue';
import { reasonTextOf } from '@/components/medicationPass/BnMedPassReasonPicker.vue';
import { atFacilityIn, effectiveOf, effectivenessEntriesOf, isPrnDose, quantityOf } from '@/common/medicationPass';
import { useAuthStore } from '@/stores/auth';

/**
 * Every as-needed dose given of one order, with whether each worked.
 *
 * Read off the administrations rather than the order, because effectiveness belongs to a dose: the
 * order's own Effectiveness field says how the medication is doing overall and is a different fact.
 * Only on the medication dialog - the face sheet and every transfer document read the order alone,
 * so none of this reaches them.
 */
const props = defineProps({
    medicationId: {
        type: String,
        required: true,
    },
    timeZone: {
        type: String,
        default: '',
    },
});

const PAGE_COUNT = 100;

// A long-running order gathers dozens of doses; the newest few are what a reviewer reads, and the
// summary line above them still counts every one.
const SHOWN_COUNT = 5;

const ELEMENTS = ['id', 'status', 'request', 'scheduledDateTime', 'category', 'effective', 'dosage', 'reasonCode', 'effectiveness'].join(',');

// Whether there is anything to show: a dose given, or a read that failed and has to say so. The
// medication dialog hides the box until then rather than show one that says nothing was given.
const emit = defineEmits(['hasContent']);

const doses = ref([]);
const loading = ref(false);
const failed = ref(false);
const showingAll = ref(false);

// Discards a read for an order the dialog has already moved off.
let latestRead = 0;

// Paged to exhaustion: a log that stopped at a cap would read as the whole story and be missing
// the oldest doses without saying so.
const readAll = async (medicationId) => {
    const found = [];
    for (let page = 1; ; page += 1) {
        // Whether there is another page is only knowable from the one before it, and without a stable
        // order a dose can move between pages mid-walk and be read twice or never.
        // eslint-disable-next-line no-await-in-loop
        const response = await $httpFhirApi.get(
            `MedicationAdministration?request=${encodeURIComponent(medicationId)}&status=completed&scheduledDateTime:missing=true&_sort=_id&_count=${PAGE_COUNT}&_page=${page}&_elements=${ELEMENTS}`,
        );
        const records = (Array.isArray(response?.data) ? response.data : []).filter((item) => item?.resourceType === 'MedicationAdministration');
        found.push(...records);
        if (records.length < PAGE_COUNT) return found;
    }
};

const load = async () => {
    const read = ++latestRead;
    loading.value = true;
    failed.value = false;
    emit('hasContent', false);
    try {
        const found = await readAll(props.medicationId);
        if (read !== latestRead) return;
        // Ordered here rather than by _sort, as the PRN screen orders its own history.
        doses.value = found.filter(isPrnDose).sort((a, b) => String(effectiveOf(b) || '').localeCompare(String(effectiveOf(a) || '')));
        emit('hasContent', doses.value.length > 0);
    } catch (err) {
        if (read !== latestRead) return;
        doses.value = [];
        failed.value = true;
        emit('hasContent', true);
        $log.error('BnMedicationEffectivenessLog: failed to load the PRN effectiveness log', `medication ${props.medicationId}`, err?.message, err?.stack);
    } finally {
        if (read === latestRead) loading.value = false;
    }
};

watch(
    () => props.medicationId,
    () => {
        showingAll.value = false;
        load();
    },
    { immediate: true },
);

const authStore = useAuthStore();

// The dialog's clock, in the account's date and time format with the zone named, as the order's
// History beside it reads: the eMAR shows the same instants on the facility's clock, which can be a
// different zone, so a bare time would disagree with it. Converted into the zone first - formatDatetime
// only labels the browser's own time with it.
const atDialogClock = (instant) => atFacilityIn(instant, `${authStore.outputDateFormat || 'MM/dd/yyyy'} ${authStore.militaryTime ? 'kk:mm' : 'h:mm a'} z`, props.timeZone);

const rows = computed(() =>
    doses.value.map((administration) => {
        return {
            id: administration.id,
            when: atDialogClock(effectiveOf(administration)) || 'Time not recorded',
            amount: quantityOf(administration?.dosage?.dose),
            reason: reasonTextOf(administration?.reasonCode?.[0]),
            entries: effectivenessEntriesOf(administration).map((entry) => ({ ...entry, stamp: [entry.authorName, atDialogClock(entry.time)].filter(Boolean).join(' · ') })),
        };
    }),
);

const shownRows = computed(() => (showingAll.value ? rows.value : rows.value.slice(0, SHOWN_COUNT)));

// Counted over every dose read, not only the ones shown, so it stays true while the list is cut short.
const summary = computed(() => {
    const notRecorded = rows.value.filter((row) => !row.entries.length).length;
    return { total: rows.value.length, recorded: rows.value.length - notRecorded, notRecorded };
});
</script>

<template>
    <div data-cy="medicationEffectivenessLog">
        <BnLoading v-if="loading" spinner data-cy="medicationEffectivenessLoading" />

        <v-alert v-else-if="failed" type="error" density="compact" class="ma-2" data-cy="medicationEffectivenessError">
            The PRN effectiveness log could not be loaded.
            <template #append>
                <v-btn variant="text" data-cy="medicationEffectivenessRetry" @click="load">Try again</v-btn>
            </template>
        </v-alert>

        <div v-else-if="!rows.length" class="text-caption ml-2 mb-2 pt-2" data-cy="medicationEffectivenessEmpty">No PRN doses given</div>

        <template v-else>
            <div class="text-body-2 px-4 pt-2 pb-1" data-cy="medicationEffectivenessSummary">
                {{ summary.total }} PRN {{ summary.total === 1 ? 'dose' : 'doses' }} · {{ summary.recorded }} recorded
                <span v-if="summary.notRecorded" class="text-warning font-weight-medium" data-cy="medicationEffectivenessSummaryOwed">
                    ·
                    <font-awesome-icon :icon="['far', 'triangle-exclamation']" class="mx-1" />{{ summary.notRecorded }} not recorded
                </span>
            </div>
            <v-divider class="mb-2" />

            <!-- Showing all can run long; it scrolls in place rather than stretching the dialog. -->
            <div :class="{ 'overflow-y-auto': showingAll }" :style="showingAll ? 'max-height: 300px' : undefined">
                <template v-for="(row, index) in shownRows" :key="row.id">
                    <v-divider v-if="index" class="my-2" />
                    <div class="px-4 py-1" :data-cy="`medicationEffectivenessDose-${row.id}`">
                        <div class="text-body-2 font-weight-bold">
                            {{ row.when }}<span v-if="row.amount"> · {{ row.amount }}</span>
                        </div>
                        <div v-if="row.reason" class="text-body-2" :data-cy="`medicationEffectivenessReason-${row.id}`">Reason: {{ row.reason }}</div>
                        <div v-for="(entry, entryIndex) in row.entries" :key="`${entry.time}-${entryIndex}`" class="text-body-2" :data-cy="`medicationEffectivenessEntry-${row.id}`">
                            Effectiveness: {{ entry.text }}
                            <span class="text-caption text-medium-emphasis">· {{ entry.stamp }}</span>
                        </div>
                        <div v-if="!row.entries.length" class="text-body-2 text-warning" :data-cy="`medicationEffectivenessNotRecorded-${row.id}`">
                            <font-awesome-icon :icon="['far', 'triangle-exclamation']" class="mr-1" />Effectiveness: Not recorded
                        </div>
                    </div>
                </template>
            </div>

            <v-btn v-if="rows.length > SHOWN_COUNT" variant="text" color="primary" class="ml-2 mt-1" data-cy="medicationEffectivenessShowAll" @click="showingAll = !showingAll">
                {{ showingAll ? 'Show fewer' : `Show all ${rows.length}` }}
            </v-btn>
        </template>
    </div>
</template>
